import * as React from 'react';
import { FieldConfiguration } from '../api/ConfigApi';
import {
	default as PremiumReportApi,
	default as PremiumReportingApi,
} from '../api/PremiumReportingApi';
import { FORM_NAME } from '../components/fastForm/data/constants';
import getPremiumReportForm from '../components/fastForm/formBuilders/getPremiumReportForm';
import {
	FormBuilder,
	FormShape,
	SaveFastForm,
} from '../components/fastForm/types';
import premiumReportUpdateBuilder from '../components/fastForm/updateBuilders/premiumReportUpdateBuilder';
import {
	PremiumReportResponse,
	PremiumReportUpdateRequest,
} from '../types/premiumReports';
import { AuthContext, AuthData } from './auth';
import { ConfigContext } from './config';
import { ErrorContext, ErrorData } from './ErrorProvider';
import { PolicyContext } from './policy';

export const PremiumReportContext =
	React.createContext<PremiumReportData>(null);

export const usePremiumReportContext = () =>
	React.useContext(PremiumReportContext);

export interface PremiumReportData {
	replaceMode: boolean;
	premiumReport: PremiumReportResponse;
	premiumReports: PremiumReportResponse[];
	premiumReportUpdateReq: PremiumReportUpdateRequest;
	setLoading?: (loading: boolean) => void;
	setReplaceMode: (replaceMode: boolean) => Promise<PremiumReportData>;
	fetchPremiumReport: (locator: string) => Promise<PremiumReportData>;
	setPremiumReport: (
		premiumReport: PremiumReportResponse,
	) => Promise<PremiumReportData>;
	fetchPremiumReports: (policyId: string) => Promise<PremiumReportData>;
	setPremiumReports: (
		premiumReports: PremiumReportResponse[],
	) => Promise<PremiumReportData>;
	setPremiumReportUpdateReq: (
		premiumReportUdpateReq: PremiumReportUpdateRequest,
	) => Promise<PremiumReportData>;
	clear: () => Promise<PremiumReportData>;
	fields: FormShape<FormBuilder>;
	updateFields?: (
		fieldsConfig: FieldConfiguration[],
		reset?: boolean,
	) => Promise<PremiumReportData>;
	saveFastForm: SaveFastForm<PremiumReportData>;
	replaceReport: SaveFastForm<PremiumReportResponse>;
}

interface Props {
	authData: AuthData;
	errorData: ErrorData;
}

export class PremiumReportProvider extends React.Component<
	Props,
	PremiumReportData
> {
	constructor(props: any, context: any) {
		super(props);
		this.state = {
			replaceMode: false,
			premiumReport: null,
			premiumReportUpdateReq: {},
			premiumReports: [],
			setReplaceMode: this.setReplaceMode,
			fetchPremiumReport: this.fetchPremiumReport,
			setPremiumReport: this.setPremiumReport,
			fetchPremiumReports: this.fetchPremiumReports,
			setPremiumReports: this.setPremiumReports,
			setPremiumReportUpdateReq: this.setPremiumReportUpdateReq,
			clear: this.clear,
			fields: null,
			updateFields: this.updateFields,
			saveFastForm: this.saveFastForm,
			replaceReport: this.replaceReport,
		};
	}

	public render() {
		return (
			<PremiumReportContext.Provider value={this.state}>
				{this.props.children}
			</PremiumReportContext.Provider>
		);
	}

	private updateFields = (
		fieldsConfig: FieldConfiguration[],
		reset = false,
	) => {
		const updatedFields: FormShape<FormBuilder> = getPremiumReportForm({
			premiumReport: {
				...this.state.premiumReport,
				fieldValues: reset ? {} : this.state.premiumReport.fieldValues,
			},
			authData: this.props.authData,
			fields: fieldsConfig,
		});
		const promise = new Promise<PremiumReportData>((resolve) => {
			this.setState({ fields: updatedFields }, () => {
				resolve(this.state);
			});
		});

		return promise;
	};

	private saveFastForm: SaveFastForm<PremiumReportData> = async (
		values,
		dirtyFields,
		_options,
	) => {
		let result = {} as PremiumReportData;
		const { authData } = this.props;
		const { premiumReportUpdateReq, premiumReport, fields, setPremiumReport } =
			this.state;
		const reqObj = premiumReportUpdateBuilder(
			dirtyFields,
			values,
			fields,
			FORM_NAME.PREMIUM_REPORT_FORM,
			authData.auth.timezone,
			premiumReportUpdateReq,
		);
		const updatedPremiumReport = await PremiumReportApi.updatePremiumReport(
			authData.auth.authorizationToken,
			premiumReport.locator,
			reqObj,
		);

		await setPremiumReport(updatedPremiumReport.data);
		result = this.state;

		return result;
	};

	private replaceReport: SaveFastForm<PremiumReportResponse> = async (
		values,
		dirtyFields,
		_options,
	) => {
		let result = {} as PremiumReportResponse;
		const { authData } = this.props;
		const {
			premiumReportUpdateReq,
			premiumReport,
			fields,
			setReplaceMode,
			setPremiumReportUpdateReq,
			setPremiumReport,
			fetchPremiumReports,
		} = this.state;
		const reqObj = premiumReportUpdateBuilder(
			dirtyFields,
			values,
			fields,
			FORM_NAME.PREMIUM_REPORT_FORM,
			authData.auth.timezone,
			premiumReportUpdateReq,
		);

		const replacedPremiumReportResponse =
			await PremiumReportApi.replacePremiumReport(
				authData.auth.authorizationToken,
				premiumReport.locator,
				reqObj,
			);
		const replacedPremiumReport = replacedPremiumReportResponse.data;

		await setReplaceMode(false);
		await setPremiumReportUpdateReq({});
		await setPremiumReport(replacedPremiumReport);
		await fetchPremiumReports(replacedPremiumReport.policyLocator);
		result = replacedPremiumReport;

		return result;
	};

	private setReplaceMode = (replaceMode: boolean) => {
		const promise = new Promise<PremiumReportData>((resolve, reject) => {
			this.setState({ replaceMode }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private fetchPremiumReport = async (locator: string) => {
		const premiumReport = await PremiumReportingApi.getPremiumReport(
			this.props.authData.auth.authorizationToken,
			locator,
		);
		return this.setPremiumReport(premiumReport.data);
	};

	private setPremiumReport = (premiumReport: PremiumReportResponse) => {
		const promise = new Promise<PremiumReportData>((resolve, reject) => {
			this.setState({ premiumReport }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private fetchPremiumReports = async (policyLocator: string) => {
		const premiumReports = await PremiumReportingApi.getPremiumReports(
			this.props.authData.auth.authorizationToken,
			policyLocator,
		);
		return this.setPremiumReports(premiumReports.data);
	};

	private setPremiumReports = (premiumReports: PremiumReportResponse[]) => {
		const promise = new Promise<PremiumReportData>((resolve, reject) => {
			this.setState({ premiumReports }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private setPremiumReportUpdateReq = (
		premiumReportUpdateReq: PremiumReportUpdateRequest,
	) => {
		const promise = new Promise<PremiumReportData>((resolve, reject) => {
			this.setState({ premiumReportUpdateReq }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private clear = () => {
		const promise = new Promise<PremiumReportData>((resolve, reject) => {
			this.setState(
				{
					premiumReport: null,
					premiumReportUpdateReq: {},
				},
				() => {
					resolve(this.state);
				},
			);
		});
		return promise;
	};
}

export const isPremiumReportUpdateReqChanged = (
	premiumReportUpdateReq: PremiumReportUpdateRequest,
	premiumReport: PremiumReportResponse,
) => {
	if (!premiumReportUpdateReq || !premiumReport) {
		return false;
	}

	return (
		premiumReportUpdateReq.invoiceDueTimestamp !==
			premiumReport.invoiceDueTimestamp ||
		premiumReportUpdateReq.endTimestamp !== premiumReport.endTimestamp
	);
};

export const isPremiumReportUpdateReqValid = (
	premiumReportUpdateReq: PremiumReportUpdateRequest,
	fields: FieldConfiguration[],
) => {
	if (
		!premiumReportUpdateReq.endTimestamp ||
		!premiumReportUpdateReq.invoiceDueTimestamp
	) {
		return false;
	}
	const updateReqFieldValues = premiumReportUpdateReq.fieldValues;
	const hasRequiredValues = !fields.some((field) => {
		if (field.optional) {
			return false;
		} else {
			return !(
				updateReqFieldValues[field.name] &&
				updateReqFieldValues[field.name]?.length &&
				updateReqFieldValues[field.name].every((v) => !!v)
			);
		}
	});

	return hasRequiredValues;
};

export function withPremiumReportContext<T>(Component: any) {
	return (
		props: Omit<
			T,
			| 'configData'
			| 'policyData'
			| 'errorData'
			| 'premiumReportData'
			| 'authData'
		>,
	) => (
		<ErrorContext.Consumer>
			{(errorData) => (
				<PremiumReportContext.Consumer>
					{(premiumReportData) => (
						<PolicyContext.Consumer>
							{(policyData) => (
								<AuthContext.Consumer>
									{(authData) => (
										<ConfigContext.Consumer>
											{(configData) => (
												<Component
													{...props}
													authData={authData}
													premiumReportData={premiumReportData}
													errorData={errorData}
													policyData={policyData}
													configData={configData}
												/>
											)}
										</ConfigContext.Consumer>
									)}
								</AuthContext.Consumer>
							)}
						</PolicyContext.Consumer>
					)}
				</PremiumReportContext.Consumer>
			)}
		</ErrorContext.Consumer>
	);
}

export function withContext<Props>(Component: any) {
	return (props: Omit<Props, 'errorData' | 'authData'>) => (
		<AuthContext.Consumer>
			{(authData) => <Component {...props} authData={authData} />}
		</AuthContext.Consumer>
	);
}

export default withContext(PremiumReportProvider);
